import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuLabel,
  ContextMenuTrigger,
} from '@repo/ui-web';

import * as React from 'react';
import { forwardRef } from 'react';
import { StateToggle } from '../../components/StateToggle';

const TriggerArea = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={`flex h-36 w-full items-center justify-center rounded-md border border-dashed border-border bg-muted/30 text-sm text-muted-foreground ${className ?? ''}`}
      {...props}
    >
      {children}
    </div>
  ),
);

function BasicDemo() {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <TriggerArea>Right-click here</TriggerArea>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-44">
        <ContextMenuItem>Back</ContextMenuItem>
        <ContextMenuItem>Forward</ContextMenuItem>
        <ContextMenuItem>Reload</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function SubmenuDemo() {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <TriggerArea>Right-click for submenu</TriggerArea>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-44">
        <ContextMenuItem>New Tab</ContextMenuItem>
        <ContextMenuItem>New Window</ContextMenuItem>
        <ContextMenuSub>
          <ContextMenuSubTrigger>More Tools</ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-48">
            <ContextMenuItem>Developer Tools</ContextMenuItem>
            <ContextMenuItem>Task Manager</ContextMenuItem>
            <ContextMenuItem>Extensions</ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuItem>Settings</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function GroupsDemo() {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <TriggerArea>Right-click for groups</TriggerArea>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-44">
        <ContextMenuLabel>Edit</ContextMenuLabel>
        <ContextMenuItem>Cut</ContextMenuItem>
        <ContextMenuItem>Copy</ContextMenuItem>
        <ContextMenuItem>Paste</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuLabel>View</ContextMenuLabel>
        <ContextMenuItem>Zoom In</ContextMenuItem>
        <ContextMenuItem>Zoom Out</ContextMenuItem>
        <ContextMenuItem>Reset Zoom</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function DestructiveDemo() {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <TriggerArea>Right-click for destructive</TriggerArea>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-44">
        <ContextMenuItem>Share</ContextMenuItem>
        <ContextMenuItem>Move to…</ContextMenuItem>
        <ContextMenuItem>Rename</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem className="text-destructive focus:text-destructive">
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

export function ContextMenuShowcase() {
  const variants = ['basic', 'submenu', 'groups', 'destructive'] as const;
  type ContextMenuVariant = (typeof variants)[number];

  const [variant, setVariant] = React.useState<ContextMenuVariant>('basic');

  const variantMap: Record<
    ContextMenuVariant,
    { title: string; render: () => React.ReactElement }
  > = {
    basic: { title: 'Basic', render: BasicDemo },
    submenu: { title: 'Submenu', render: SubmenuDemo },
    groups: { title: 'Groups', render: GroupsDemo },
    destructive: { title: 'Destructive', render: DestructiveDemo },
  };

  const activeVariant = variantMap[variant];
  const ActiveDemo = activeVariant.render;

  return (
    <div className="space-y-4">
      <StateToggle
        options={variants}
        value={variant}
        onChange={setVariant}
        label="Variant"
        formatLabel={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
      />
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-foreground">{activeVariant.title}</h4>
        <ActiveDemo />
      </div>
    </div>
  );
}
